import * as React from 'react';

import Avatar from 'components/Avatar';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
// @ts-ignore
import Divider from 'components/Divider';
import Image from 'components/Image';
import MediaObject from 'components/MediaObject';
import Icon, { IconName } from 'components/Icon';
import Text from 'components/Text';
import TooltipTrigger from 'components/TooltipTrigger';

export function EnableDisableToggle(props: {
  deviceIsEnabled: boolean;
  onEnableOrDisableDevice: () => void;
}) {
  const image = props.deviceIsEnabled
    ? require('assets/images/notifications/bell.png')
    : require('assets/images/notifications/bell-off.png');
  const text = props.deviceIsEnabled ? 'On' : 'Off';
  const textColor = props.deviceIsEnabled ? 'brand' : 'thunder';
  return (
    <Button onClick={props.onEnableOrDisableDevice} level="secondary" color="polar-bear" full>
      <MediaObject gutter="xsmall" center inline>
        <Image width={16} src={image} />
        <Text color={textColor}>{text}</Text>
      </MediaObject>
    </Button>
  );
}

export type Props = {
  deviceType: string;
  deviceIsEnabled?: boolean;
  deviceCanBeEnabledOrDisabled?: boolean;
  deviceIsOnlyLoginDevice?: boolean;
  deviceIsRostered?: boolean;
  deviceExists: boolean;
  actionForNonexistentDevice?: {
    label: string;
    action: () => void;
  };
  primaryTextLabel: React.ReactNode;
  secondaryTextLabel: React.ReactNode;
  onRemoveDevice?: () => void;
  onEnableOrDisableDevice?: () => void;
};

export default class DeviceSettingsItemTemplate extends React.Component<Props> {
  getIconForDeviceType = (): IconName => {
    const { deviceType } = this.props;

    switch (deviceType) {
      case 'apns':
        return 'mobile';
      case 'gcm':
        return 'mobile';
      case 'landline':
        return 'landline-phone';
      case 'sms':
        return 'phone';
      case 'email':
        return 'mail';
      case 'desktop':
        return 'computer';
      default:
        return 'question-circle';
    }
  };

  renderRemovalButton = () => {
    const { deviceIsRostered, onRemoveDevice, deviceIsOnlyLoginDevice } = this.props;

    if (deviceIsRostered) {
      return (
        <TooltipTrigger
          data-test="rostered-device-tooltip"
          tooltip="This device is rostered in your school or district's database. Contact your school or district administrator to remove."
          placement="top"
          alignment="middle">
          <span>
            <Icon name="link-linked" size="large" color="jungle" />
          </span>
        </TooltipTrigger>
      );
    } else if (deviceIsOnlyLoginDevice) {
      return (
        <TooltipTrigger
          data-test="device-is-only-login-tooltip"
          tooltip="Add a different email address to remove this device"
          placement="top"
          alignment="middle">
          <span>
            <Button level="text" color="thunder" disabled>
              <Icon size="medium" name="trash" />
            </Button>
          </span>
        </TooltipTrigger>
      );
    } else if (!onRemoveDevice) {
      return null;
    } else {
      return (
        <TooltipTrigger data-test="remove-device-tooltip" tooltip="Remove device">
          <Button
            data-test="remove-device-button"
            onClick={onRemoveDevice}
            level="text"
            color="thunder">
            <Icon size="medium" name="trash" />
          </Button>
        </TooltipTrigger>
      );
    }
  };

  renderEnablementAndRemovalActions = () => {
    const { deviceCanBeEnabledOrDisabled, deviceIsEnabled, onEnableOrDisableDevice } = this.props;

    if (deviceCanBeEnabledOrDisabled && onEnableOrDisableDevice) {
      return (
        <Buttons align="right" fullButtonIndex={0} margin="large">
          <EnableDisableToggle
            deviceIsEnabled={!!deviceIsEnabled}
            onEnableOrDisableDevice={onEnableOrDisableDevice}
          />
          {this.renderRemovalButton()}
        </Buttons>
      );
    }

    return <Buttons align="right">{this.renderRemovalButton()}</Buttons>;
  };

  render() {
    const {
      primaryTextLabel,
      secondaryTextLabel,
      deviceExists,
      actionForNonexistentDevice,
    } = this.props;
    const avatarComponent = !deviceExists ? (
      <Avatar iconName={this.getIconForDeviceType()} size="large" color="grey" />
    ) : (
      <Avatar iconName={this.getIconForDeviceType()} size="large" />
    );

    const actionComponent =
      !deviceExists && actionForNonexistentDevice ? (
        <Button
          level="secondary"
          full
          onClick={actionForNonexistentDevice.action}
          data-test="nonexistent-device-action">
          {actionForNonexistentDevice.label}
        </Button>
      ) : (
        this.renderEnablementAndRemovalActions()
      );

    return (
      <MediaObject gutter="small">
        {avatarComponent}
        <Text lineHeight="large">
          <MediaObject figure="right">
            <div>
              <Text size="medium" element="div">
                {primaryTextLabel}
              </Text>
              <Text color="thunder">{secondaryTextLabel}</Text>
            </div>
            <div style={{ width: 120 }}>{actionComponent}</div>
          </MediaObject>
          <Divider size="small" />
        </Text>
      </MediaObject>
    );
  }
}
